import {
  BadgeOutlined,
  CalendarMonthOutlined,
  CameraAlt,
  CheckCircle,
  EmailOutlined,
  ErrorOutline,
  HomeOutlined,
  Save,
  UploadFile,
  WarningAmber,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { DOC_TYPES } from "../config/config";
import { useProfile } from "./hooks/useProfile";

const GREEN = "#00C853";
const DARK = "#0D1420";

const getInitials = (name?: string) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?";
  return ((parts[0][0] ?? "") + (parts[1][0] ?? "")).toUpperCase();
};

const ProfilePage: React.FC = () => {
  const {
    user, userData, form, handlePickPhoto, fileInputRef,
    uploading, reloading, handleChange, handleBlur, errors,
    saving, handleSave, docFileRef, selfieFileRef,
    handlePickDoc, handlePickSelfie, uploadingDoc, uploadingSelfie,
    docPreview, selfiePreview, handlePhotoInput,
  } = useProfile();

  const isVerified = userData.data?.isVerified;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 4 }}>

      {/* Header perfil */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${isVerified ? GREEN + "44" : "#e0e0e0"}`, overflow: "hidden" }}>
        <Box sx={{ bgcolor: DARK, p: 3, textAlign: "center", position: "relative" }}>
          <Box sx={{ position: "relative", display: "inline-block", mb: 1.5 }}>
            <Avatar
              src={user?.photoURL || ""}
              sx={{ width: 80, height: 80, bgcolor: `${GREEN}22`, border: `3px solid ${GREEN}`, color: GREEN, fontWeight: 800, fontSize: 28 }}
            >
              {!user?.photoURL && getInitials(form.fullName)}
            </Avatar>
            {(uploading || reloading) && (
              <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", borderRadius: "50%", bgcolor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={28} sx={{ color: "white" }} />
              </Box>
            )}
            <IconButton onClick={handlePickPhoto} size="small" sx={{ position: "absolute", bottom: -4, right: -4, bgcolor: GREEN, color: "white", width: 28, height: 28, "&:hover": { bgcolor: "#00b048" } }}>
              <CameraAlt sx={{ fontSize: 16 }} />
            </IconButton>
            <input type="file" accept="image/*" ref={fileInputRef} onChange={handlePhotoInput} style={{ display: "none" }} />
          </Box>

          <Typography sx={{ fontWeight: 800, fontSize: 20, color: "white", mb: 0.5 }}>
            {form.fullName || "Anónimo"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#8899AA", mb: 1.5 }}>
            {user?.email || user?.phoneNumber || "Sin contacto"}
          </Typography>

          <Chip
            label={isVerified ? "Verificado" : "Pendiente de verificación"}
            icon={isVerified ? <CheckCircle sx={{ fontSize: 16 }} /> : <ErrorOutline sx={{ fontSize: 16 }} />}
            size="small"
            sx={{
              bgcolor: isVerified ? `${GREEN}33` : "#f59e0b33",
              color: isVerified ? GREEN : "#f59e0b",
              fontWeight: 700,
              border: `1px solid ${isVerified ? GREEN : "#f59e0b"}44`,
              "& .MuiChip-icon": { color: isVerified ? GREEN : "#f59e0b" },
            }}
          />
        </Box>
      </Card>

      {/* Verificación KYC */}
      {!isVerified && (
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #f59e0b44", overflow: "hidden" }}>
          <Box sx={{ bgcolor: "#f59e0b18", px: 2.5, py: 1.5, borderBottom: "1px solid #f59e0b22" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><WarningAmber sx={{ color: "#f59e0b", fontSize: 18 }} /><Typography sx={{ fontWeight: 700, fontSize: 14, color: "#f59e0b" }}>Verificación pendiente</Typography></Box>
            <Typography sx={{ fontSize: 12, color: "#8899AA" }}>Sube tu documento y selfie para verificar tu identidad</Typography>
          </Box>
          <CardContent sx={{ bgcolor: "#f7f9f7", p: 2 }}>
            <Stack spacing={2}>
              {/* Documento */}
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: DARK, mb: 1 }}>Foto de tu documento</Typography>
                {userData.data?.docPhotoURL && !docPreview ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, bgcolor: "#00C85311", borderRadius: 2, border: "1px solid #00C85333" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><CheckCircle sx={{ color: "#00C853", fontSize: 16 }} /><Typography sx={{ fontSize: 13, color: "#00C853", fontWeight: 700 }}>Documento subido</Typography></Box>
                    <Button size="small" onClick={() => docFileRef.current?.click()} sx={{ fontSize: 11, textTransform: "none", color: "#667788", ml: "auto" }}>Cambiar</Button>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<UploadFile />}
                    onClick={() => docFileRef.current?.click()}
                    disabled={uploadingDoc}
                    sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, bgcolor: DARK, color: "white", boxShadow: "none", "&:hover": { bgcolor: "#1a2840" } }}
                  >
                    {uploadingDoc ? "Subiendo..." : "Subir documento"}
                  </Button>
                )}
                <input type="file" accept="image/*" capture="environment" ref={docFileRef} onChange={handlePickDoc} style={{ display: "none" }} />
                {docPreview && <Box component="img" src={docPreview} alt="Documento" sx={{ mt: 1, maxWidth: 200, borderRadius: 2, border: "1px solid #e0e0e0", display: "block" }} />}
                {errors.docPhoto && <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>{errors.docPhoto}</Alert>}
              </Box>

              {/* Selfie */}
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: DARK, mb: 1 }}>🤳 Selfie con tu documento</Typography>
                {userData.data?.selfiePhotoURL && !selfiePreview ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, bgcolor: "#00C85311", borderRadius: 2, border: "1px solid #00C85333" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><CheckCircle sx={{ color: "#00C853", fontSize: 16 }} /><Typography sx={{ fontSize: 13, color: "#00C853", fontWeight: 700 }}>Selfie subida</Typography></Box>
                    <Button size="small" onClick={() => selfieFileRef.current?.click()} sx={{ fontSize: 11, textTransform: "none", color: "#667788", ml: "auto" }}>Cambiar</Button>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<UploadFile />}
                    onClick={() => selfieFileRef.current?.click()}
                    disabled={uploadingSelfie}
                    sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, bgcolor: DARK, color: "white", boxShadow: "none", "&:hover": { bgcolor: "#1a2840" } }}
                  >
                    {uploadingSelfie ? "Subiendo..." : "Subir selfie"}
                  </Button>
                )}
                <input type="file" accept="image/*" capture="user" ref={selfieFileRef} onChange={handlePickSelfie} style={{ display: "none" }} />
                {selfiePreview && <Box component="img" src={selfiePreview} alt="Selfie" sx={{ mt: 1, maxWidth: 200, borderRadius: 2, border: "1px solid #e0e0e0", display: "block" }} />}
                {errors.selfiePhoto && <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>{errors.selfiePhoto}</Alert>}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Información del perfil */}
      <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #f0f0f0" }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f0f0f0" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: DARK }}>Información personal</Typography>
          <Typography sx={{ fontSize: 12, color: "#8899AA" }}>Completa todos los campos para verificar tu cuenta</Typography>
        </Box>
        <CardContent sx={{ bgcolor: "#f7f9f7", p: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Nombre completo"
                value={form.fullName} onChange={handleChange("fullName")} onBlur={handleBlur("fullName")}
                error={!!errors.fullName} helperText={errors.fullName}
                disabled={isVerified}
                sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                select fullWidth required label="Tipo doc."
                value={form.documentType || ""} onChange={handleChange("documentType")} onBlur={handleBlur("documentType")}
                error={!!errors.documentType} helperText={errors.documentType}
                disabled={isVerified}
                sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              >
                {DOC_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={7}>
              <TextField
                fullWidth required label="Número de documento"
                value={form.document || ""} onChange={handleChange("document")} onBlur={handleBlur("document")}
                error={!!errors.document} helperText={errors.document}
                disabled={isVerified}
                sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Dirección (opcional)"
                value={form.address || ""} onChange={handleChange("address")} onBlur={handleBlur("address")}
                error={!!errors.address} helperText={errors.address}
                sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Correo electrónico (opcional)"
                value={form.emailOptional || ""} onChange={handleChange("emailOptional")} onBlur={handleBlur("emailOptional")}
                error={!!errors.emailOptional} helperText={errors.emailOptional}
                sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Fecha de nacimiento"
                value={form.birthDate || ""} onChange={handleChange("birthDate")} onBlur={handleBlur("birthDate")}
                type="date" InputLabelProps={{ shrink: true }}
                error={!!errors.birthDate} helperText={errors.birthDate}
                disabled={isVerified}
                sx={{ bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Botón guardar */}
      <Button
        fullWidth variant="contained"
        onClick={handleSave}
        disabled={saving || uploading || reloading}
        sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, fontSize: 15, bgcolor: GREEN, color: "white", py: 1.5, boxShadow: "none", "&:hover": { bgcolor: "#00b048" } }}
      >
        {saving ? <CircularProgress size={22} sx={{ color: "white" }} /> : <><Save sx={{ mr: 1, fontSize: 18 }} />Guardar cambios</>}
      </Button>
    </Box>
  );
};

export default ProfilePage;
